// @ts-nocheck
/**
 * resolveItemClasses.ts — Resolve Template Class ids for an item via its element (DEC-066).
 *
 * Class membership is authored on ElementDefinition.classIds (not ItemDefinition).
 * Effective membership expands single-parent ancestry so `itemHasClass(parent)` is true
 * when a child class is attached. Unresolvable classIds (missing definition or cycle)
 * are omitted — same rule as flattenTemplateClasses.
 */

import type { ItemDefinition } from '../../../../../shims/appStub';
import type { TemplateClassDefinition, TemplateDocument } from '../../types/canonical';
import { resolveClassAncestry } from '../loader/flattenTemplateClasses';
import {
  isDefaultElementId,
  isDefaultLikeTemplateId,
  normalizeTemplateElementIdForLookup,
  shouldUseGenericCanvasItem,
} from '../../../utils/TemplateIdUtils';
import { TemplateRuntimeRegistry } from '../../../../../shims/TemplateRuntimeRegistry';

/** Minimal template shape for class membership (paint / preview / registry). */
export type TemplateClassLookup = {
  metadata?: { templateId?: string; primaryElementId?: string };
  elements: readonly { id: string; classIds?: readonly string[] }[];
  classes?: readonly { id: string; extendsClassId?: string }[];
};

function classMapFromTemplate(
  template: TemplateClassLookup,
): Map<string, TemplateClassDefinition> {
  const map = new Map<string, TemplateClassDefinition>();
  for (const cls of template.classes ?? []) {
    map.set(cls.id, {
      id: cls.id,
      categoryIds: [],
      extendsClassId: cls.extendsClassId,
    });
  }
  return map;
}

function resolveElementForClassLookup(
  template: TemplateClassLookup,
  elementId?: string | null,
): { id: string; classIds?: readonly string[] } | undefined {
  const elements = template.elements ?? [];
  if (elements.length === 0) {
    return undefined;
  }

  if (isDefaultElementId(elementId) && !isDefaultLikeTemplateId(template.metadata?.templateId)) {
    return undefined;
  }

  const lookupId = normalizeTemplateElementIdForLookup(elementId);
  const direct = elements.find((el) => el.id === lookupId);
  if (direct) {
    return direct;
  }

  const primaryId = template.metadata?.primaryElementId;
  if (primaryId) {
    const primary = elements.find((el) => el.id === primaryId);
    if (primary) {
      return primary;
    }
  }

  return elements[0];
}

function templateFromRegistry(item: ItemDefinition): TemplateClassLookup | undefined {
  const templateId = item.templateId?.trim();
  if (!templateId) return undefined;
  return TemplateRuntimeRegistry.getTemplate(templateId);
}

/**
 * Effective class ids for an item: each attached classId expanded root→child,
 * first-seen order, unique. Skips unresolvable chains (missing or cyclic).
 * `templateOverride` is used by paint/preview; omit to use TemplateRuntimeRegistry.
 */
export function resolveEffectiveClassIdsForItem(
  item: ItemDefinition,
  templateOverride?: TemplateClassLookup | TemplateDocument | null,
): string[] {
  const template = templateOverride ?? templateFromRegistry(item);
  if (!template) return [];
  if (shouldUseGenericCanvasItem(item.templateId, item.templateElementId)) {
    return [];
  }

  const element = resolveElementForClassLookup(template, item.templateElementId);
  const attached = element?.classIds ?? [];
  if (attached.length === 0) return [];

  const classById = classMapFromTemplate(template);
  const seen = new Set<string>();
  const out: string[] = [];

  for (const classId of attached) {
    const ancestry = resolveClassAncestry(classId, classById);
    if (!ancestry) {
      continue;
    }
    for (const cls of ancestry) {
      if (seen.has(cls.id)) continue;
      seen.add(cls.id);
      out.push(cls.id);
    }
  }

  return out;
}

export function itemHasClassId(
  item: ItemDefinition,
  classId: string,
  templateOverride?: TemplateClassLookup | TemplateDocument | null,
): boolean {
  const needle = classId.trim();
  if (!needle) return false;
  return resolveEffectiveClassIdsForItem(item, templateOverride).includes(needle);
}
